import { useCallback } from "react";
import { useSelector } from "react-redux";
import jwtDecode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "$store";

import { UserRoles } from "$utils/typeConstants";
import { authState } from "$reducers";
import authAPi from "$api/authApi";
import {
  login as actionLogin,
  load,
  logout as actionLogout,
  setToken as actionSetToken,
} from "$actions/auth";
import { setFilter } from "$actions/app";
import useCompany from "./useCompany";

const useAuth = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector(authState);
  const navigate = useNavigate();
  const { isClient: isClientCompany, isContributor: isContributorCompany } =
    useCompany();
  const login = async (email: string, password: string, persist = false) => {
    dispatch(load());
    const resultToken = (await authAPi.getToken(email, password).catch((e) => {
      logout();
      throw e;
    })) as any;
    callbackAfterLogin(email, password, persist, resultToken);
  };

  const modifyPasswordAndLogin = async (
    userId: string,
    email: string,
    password: string,
    persist = false,
    oldPassword?: string
  ) => {
    dispatch(load());
    try {
      let resultToken: any;
      if (!oldPassword) {
        resultToken = await authAPi.changePassword(userId, password);
      } else {
        resultToken = await authAPi.changePasswordOnSetting(
          userId,
          email,
          oldPassword,
          password
        );
      }
      callbackAfterLogin(email, password, persist, resultToken.data);
    } catch (e) {
      logout();
      throw e;
    }
  };

  const verifyPassword = async (
    userId: string,
    email: string,
    password: string
  ) => {
    return authAPi.verifyPassword(userId, email, password);
  };

  const callbackAfterLogin = async (
    email: string,
    password: string,
    persist: boolean,
    resultToken: any
  ) => {
    dispatch(
      actionLogin(email, resultToken.access_token, {
        ...resultToken.userInfo,
        permissions: getPermission(resultToken.access_token),
      })
    );
    if (
      auth?.user && // this needs to be checked, may create a bug
      !isAdmin() &&
      !isContributor() &&
      resultToken?.userInfo?.userCompany
    ) {
      dispatch(setFilter(resultToken.userInfo.userCompany));
    }
    localStorage.setItem("access_token", resultToken.access_token);
    if (persist)
      window.localStorage.setItem("refresh_token", resultToken?.refresh_token);
    setToken(resultToken.access_token);
  };

  const setAuthState = useCallback(
    async (authStateData: any) => {
      dispatch(
        actionLogin(
          authStateData?.user?.email,
          authStateData?.token,
          authStateData?.user?.userInfo
        )
      );
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    dispatch(actionLogout());
    dispatch(setFilter(null));
    authAPi
      .revokeRefreshToken()
      .then(() => window.localStorage.removeItem("refresh_token"))
      .catch(() => window.localStorage.removeItem("refresh_token"));
    window.localStorage.removeItem("access_token");
    window.localStorage.removeItem("CatalogueidCompany");
    window.localStorage.removeItem("openedSidebar");

    navigate("/connexion");
  }, [dispatch, navigate]);

  // get new access token TODO: use Refresh token
  const getNewToken = useCallback(
    async (authStateLS: any) => {
      try {
        const tokenData = await authAPi.getRefreshToken();
        if (tokenData?.data?.access_token) {
          localStorage.setItem("access_token", tokenData?.data?.access_token);
          setAuthState({
            ...authStateLS,
            token: tokenData?.data?.access_token,
          });
          // setToken(tokenData?.data?.access_token);
        } else {
          logout();
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    },
    [logout, setAuthState]
  );

  const getPermission = (tk?: any) => {
    try {
      const perm = jwtDecode(tk);
      return (perm as any)?.permissions || [];
    } catch {
      return [];
    }
  };

  const isAdmin = useCallback(() => {
    const localStorageAuth = localStorage.getItem("authState");
    if (!localStorageAuth && !auth) return false;
    if (localStorageAuth) {
      const authStateData = JSON.parse(localStorageAuth);
      const permissions = authStateData?.user?.userInfo?.permissions || [];
      return !!permissions.find((e: string) => e === "read:protected");
    }
    if (auth) {
      const permissions = auth?.user?.userInfo?.permissions || [];
      return !!permissions.find((e: string) => e === "read:protected");
    }
    return false;
  }, [auth]);

  const isClient = useCallback(() => {
    const companyTypes = auth?.user?.userInfo?.userCompany?.companyTypes;
    if (!companyTypes) return false;
    return isClientCompany(companyTypes);
  }, [auth?.user?.userInfo?.userCompany, isClientCompany]);

  const isClientOf = useCallback(
    (companyId) => {
      const myCompanyId = auth?.user?.userInfo?.userCompany?._id;
      if (!myCompanyId) return false;
      return isClient() && myCompanyId === companyId;
    },
    [auth?.user?.userInfo?.userCompany, isClient]
  );

  const isContributor = useCallback(() => {
    const companyTypes = auth?.user?.userInfo?.userCompany?.companyTypes;
    if (!companyTypes) return false;
    return isContributorCompany(companyTypes);
  }, [auth?.user?.userInfo?.userCompany, isContributorCompany]);

  const isContributorOf = useCallback(
    (companyId: string) => {
      const contributorOf = auth?.user?.userInfo?.userCompany?.contributorOf;
      if (!Array.isArray(contributorOf) || contributorOf.length === 0)
        return false;
      return isContributor() && contributorOf.includes(companyId);
    },
    [auth?.user?.userInfo?.userCompany?.contributorOf, isContributor]
  );

  const setToken = (token: string) => {
    dispatch(actionSetToken(token));
  };
  return {
    login,
    logout,
    getNewToken,
    isAdmin,
    isClient,
    isClientOf,
    isContributor,
    isContributorOf,
    userRole: isAdmin() ? UserRoles.ADMIN : UserRoles.USER,
    setToken,
    modifyPasswordAndLogin,
    verifyPassword,
    ...auth,
    token: localStorage.getItem("access_token") || auth?.token || "",
    setAuthState,
    getPermission,
  };
};

export default useAuth;
